'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cloneElement, isValidElement, useId, useRef, useState } from 'react';
import { cn } from '../../lib/cn.js';
import styles from './Tooltip.module.css';
/** Supplementary hint shown on hover AND keyboard focus (never hover-only),
 * `role="tooltip"` wired via `aria-describedby` (ux.md, CLAUDE_DESIGN.md §9). */
export function Tooltip({ content, placement = 'top', children, forceOpen = false }) {
    const [open, setOpen] = useState(false);
    const timeoutRef = useRef(null);
    const id = useId();
    const visible = forceOpen || open;
    function show() {
        timeoutRef.current = setTimeout(() => setOpen(true), 350);
    }
    function hide() {
        if (timeoutRef.current)
            clearTimeout(timeoutRef.current);
        setOpen(false);
    }
    if (!isValidElement(children))
        return children;
    // eslint-disable-next-line react-hooks/refs -- cloneElement merges the
    // hover/focus handlers + aria-describedby onto the trigger element itself
    // (the standard accessible-tooltip pattern so AT reads the description
    // off the actual focusable control, e.g. Radix's Tooltip.Trigger); it
    // does not read or write a ref's `.current` during render.
    const trigger = cloneElement(children, {
        'aria-describedby': visible ? id : undefined,
        onMouseEnter: show,
        onMouseLeave: hide,
        onFocus: show,
        onBlur: hide,
    });
    return (_jsxs("span", { className: styles['wrapper'], children: [trigger, visible && (_jsx("span", { role: "tooltip", id: id, className: cn(styles['tooltip'], styles[placement]), children: content }))] }));
}
